import { debounce, delay } from 'lodash/fp';
import type { FC} from 'react';
import { Component, memo } from 'react';
import type { FormSpyRenderProps } from 'react-final-form';
import { FormSpy } from 'react-final-form';

const INITIAL_BACKOFF_MS = 100;

const promiseDelay = (delayMs: number): Promise<any> => new Promise(delay(delayMs));

type AutoSaverProps = FormSpyRenderProps & {
    keepDirtyOnReinitialize: boolean;
    debounceTime: number;
};

class AutoSaver extends Component<AutoSaverProps> {
    backoff: number = INITIAL_BACKOFF_MS;

    promise: Promise<object | void | undefined> | undefined = Promise.resolve();

    componentDidUpdate = (prevProps: FormSpyRenderProps) => {
        const { values, submitFailed } = this.props;
        const formChanged = prevProps.values !== values;
        if (formChanged || submitFailed) {
            this.save(formChanged);
        }
    };

    save = debounce(this.props.debounceTime, async (formChanged: boolean) => {
        await this.promise;
        const { form, dirty, dirtySinceLastSubmit, submitFailed, submitSucceeded, keepDirtyOnReinitialize } =
            this.props;
        if (formChanged) {
            this.backoff = INITIAL_BACKOFF_MS;
        }
        if (submitFailed) {
            // Staggered retry.
            this.promise = promiseDelay(this.backoff).then(form.submit);
            this.backoff = this.backoff * 2;
            return;
        }
        const hasUnsavedChanges = keepDirtyOnReinitialize ? (!submitSucceeded && dirty) || dirtySinceLastSubmit : dirty;
        this.promise = hasUnsavedChanges ? form.submit() : Promise.resolve();
    });

    render = () => null;
}

type Props = {
    /**
     * Should match the overarching <Form> component.
     */
    keepDirtyOnReinitialize: boolean;
    debounceTime?: number;
};

const FormAutoSave: FC<Props> = memo(({ keepDirtyOnReinitialize, debounceTime = 1000 }) => (
    <FormSpy
        subscription={{
            dirty: true,
            dirtySinceLastSubmit: true,
            submitFailed: true,
            submitSucceeded: true,
            values: true,
        }}
        component={renderProps => (
            <AutoSaver {...renderProps} keepDirtyOnReinitialize={keepDirtyOnReinitialize} debounceTime={debounceTime} />
        )}
    />
));

export default FormAutoSave;
